import React, { useState, useEffect, useCallback } from 'react';
import API from '../../services/api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const SaleBikeModal = ({ onClose, onRefresh }) => {
    const [step, setStep] = useState(1);
    const [bikes, setBikes] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [taxRate, setTaxRate] = useState(0);

    const [selectedBike, setSelectedBike] = useState(null);
    const [selectedCust, setSelectedCust] = useState(null);
    const [regOption, setRegOption] = useState('existing'); 
    const [customRegNo, setCustomRegNo] = useState('');

    const [salePrice, setSalePrice] = useState(''); 
    const [saleType, setSaleType] = useState('Full'); 
    const [totalMonths, setTotalMonths] = useState(12);

    // Split Payment Rows (Cash & Bank Options Only)
    const [payments, setPayments] = useState([
        { method: 'Cash', account_id: '', amount: '' }
    ]);

    const fetchData = useCallback(async () => {
        try {
            const taxRes = await API.get('/v1/tax-settings');
            if (taxRes.data.success) setTaxRate(taxRes.data.percentage);

            const accRes = await API.get('/v1/purchase/payment-accounts');
            if (accRes.data && accRes.data.success) {
                setAccounts(accRes.data.data || []);
            }
        } catch (err) { 
            console.error(err); 
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleBikeSearch = async (val) => {
        if (val.length > 0) {
            try {
                const res = await API.get(`/v1/sales/available-bikes?search=${val}`);
                setBikes(res.data.data || []);
            } catch (err) { console.error(err); }
        } else {
            setBikes([]);
        }
    };

    const handleCustSearch = async (val) => {
        if (val.length > 0) {
            try {
                const res = await API.get(`/v1/sales/search-customer?query=${val}`);
                setCustomers(res.data.data || []);
            } catch (err) { console.error(err); }
        } else {
            setCustomers([]);
        }
    };

    const gstAmount = Math.round((parseFloat(salePrice || 0) * taxRate) / 100);
    const totalPrice = Math.round(parseFloat(salePrice || 0) + gstAmount);
    
    const totalPaid = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
    const remainingBalance = Math.round(totalPrice - totalPaid);
    const monthlyAmount = totalMonths > 0 ? (remainingBalance / totalMonths) : 0;

    const addPaymentRow = () => {
        setPayments([...payments, { method: 'Cash', account_id: '', amount: '' }]);
    };

    const removePaymentRow = (index) => {
        const list = [...payments];
        list.splice(index, 1);
        setPayments(list);
    };

    const handlePaymentChange = (index, field, value) => {
        const list = [...payments];
        list[index][field] = value;
        if (field === 'method') {
            list[index]['account_id'] = ''; 
        }
        setPayments(list);
    };

    const generateInvoice = () => {
        const doc = new jsPDF();
        const timestamp = new Date().toLocaleString();
        const invoiceNo = `INV-${Math.floor(1000 + Math.random() * 9000)}`;

        doc.setFillColor(15, 23, 42); 
        doc.rect(0, 0, 210, 45, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(24);
        doc.setFont("helvetica", "bold");
        doc.text('UNITED MOTORS SHOWROOM', 105, 18, { align: 'center' });
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text(`Invoice No: ${invoiceNo}  |  Date: ${timestamp}`, 105, 28, { align: 'center' });

        doc.setTextColor(30, 41, 59);
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text('CUSTOMER DETAILS', 14, 55);
        doc.text('BIKE SPECIFICATIONS', 115, 55);
        doc.setDrawColor(226, 232, 240);
        doc.line(14, 58, 196, 58);

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text(`Name: ${selectedCust.full_name}`, 14, 65);
        doc.text(`Contact: ${selectedCust.phone}`, 14, 71);
        doc.text(`Model: ${selectedBike.model_name}`, 115, 65);
        doc.text(`Engine #: ${selectedBike.engine_number}`, 115, 71);
        doc.text(`Chassis #: ${selectedBike.chassis_number}`, 115, 77);

        const paymentSummaryText = payments.map(p => `${p.method}: Rs. ${parseFloat(p.amount || 0).toLocaleString()}`).join(', ');

        autoTable(doc, {
            startY: 90,
            theme: 'grid',
            headStyles: { fillColor: [37, 99, 235], fontSize: 10 },
            head: [['Description of Charges', 'Amount (PKR)']],
            body: [
                ['Vehicle Base Price', `Rs. ${parseFloat(salePrice || 0).toLocaleString()}`],
                [`General Sales Tax (GST @ ${taxRate}%)`, `Rs. ${gstAmount.toLocaleString()}`],
                ['Total Payable Amount (Net)', `Rs. ${totalPrice.toLocaleString()}`],
                ['Payment Modes & Breakdown', paymentSummaryText || 'Rs. 0'],
                ['Total Amount Paid', `Rs. ${totalPaid.toLocaleString()}`],
                ['Remaining Balance / Due', `Rs. ${remainingBalance.toLocaleString()}`],
            ],
            styles: { fontSize: 10, cellPadding: 5 }
        });

        doc.save(`Invoice_${selectedCust.full_name.replace(/\s+/g, '_')}.pdf`);
    };

    const handleFinalSubmit = async (shouldPrint = false) => {
        if (!salePrice || !selectedBike || !selectedCust) {
            alert("Please fill in all required fields."); 
            return;
        }

        for (let p of payments) {
            if (parseFloat(p.amount || 0) > 0 && !p.account_id) {
                alert(`Please select an account for the ${p.method} payment row.`);
                return;
            }
        }

        let finalRegNo = regOption === 'existing' ? selectedBike.reg_number : (regOption === 'new' ? customRegNo : 'Unregistered');

        const payload = {
            customer_id: selectedCust.id,
            bike_id: selectedBike.id,
            base_price: parseFloat(salePrice), 
            down_payment: totalPaid, 
            sale_type: saleType,
            payments: payments.filter(p => parseFloat(p.amount || 0) > 0),
            registration_no: finalRegNo,
            sale_date: new Date().toISOString().split('T')[0],
            total_months: saleType === 'Installment' ? parseInt(totalMonths) : 0,
            monthly_amount: parseFloat(monthlyAmount)
        };

        try {
            const res = await API.post('/v1/sales', payload);
            if (res.data.success) {
                if (shouldPrint) {
                    generateInvoice(); 
                    alert("✅ Sale processed successfully & Invoice downloaded!");
                } else {
                    alert("✅ Sale posted successfully!");
                }
                onRefresh(); 
                onClose();
            }
        } catch (err) { 
            alert("Transaction Error: " + (err.response?.data?.error || "Failed to process sale.")); 
        }
    };

    return (
        <div style={styles.overlay}>
            <div style={styles.modal}>
                <button onClick={onClose} style={styles.closeBtn}>&times;</button>
                <div style={styles.stepper}>
                    <div style={step >= 1 ? styles.stepActive : styles.stepInactive}>1. Select Bike</div>
                    <div style={step >= 2 ? styles.stepActive : styles.stepInactive}>2. Select Customer</div>
                    <div style={step >= 3 ? styles.stepActive : styles.stepInactive}>3. Registration</div>
                    <div style={step >= 4 ? styles.stepActive : styles.stepInactive}>4. Billing & Payment</div>
                </div>

                {step === 1 && (
                    <div style={styles.body}>
                        <h4 style={styles.title}>🏍️ Search & Select Bike (By Engine or Chassis Number)</h4>
                        <input 
                            placeholder="Type Engine # or Chassis # here..." 
                            onChange={(e) => handleBikeSearch(e.target.value)} 
                            style={styles.inputLarge} 
                            autoFocus 
                        />
                        <div style={styles.list}>
                            {bikes.map(b => (
                                <div key={b.id} onClick={() => { setSelectedBike(b); setStep(2); }} style={styles.cardItem}>
                                    <div>
                                        <strong style={{fontSize: '15px', color: '#1e293b'}}>{b.model_name}</strong>
                                        <div style={{fontSize:'13px', color:'#64748b', marginTop:'4px'}}>
                                            Engine #: <b>{b.engine_number}</b> | Chassis #: <b>{b.chassis_number}</b>
                                        </div>
                                    </div>
                                    <span style={styles.selectTag}>Select &rarr;</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div style={styles.body}>
                        <h4 style={styles.title}>👤 Search & Select Customer</h4>
                        <input 
                            placeholder="Type Customer Name or Phone Number..." 
                            onChange={(e) => handleCustSearch(e.target.value)} 
                            style={styles.inputLarge} 
                            autoFocus 
                        />
                        <div style={styles.list}>
                            {customers.map(c => (
                                <div key={c.id} onClick={() => { setSelectedCust(c); setStep(3); }} style={styles.cardItem}>
                                    <div>
                                        <strong style={{fontSize: '15px', color: '#1e293b'}}>{c.full_name}</strong>
                                        <div style={{fontSize:'13px', color:'#64748b', marginTop:'4px'}}>Phone: <b>{c.phone}</b></div>
                                    </div>
                                    <span style={styles.selectTag}>Select &rarr;</span>
                                </div>
                            ))}
                        </div>
                        <button onClick={() => setStep(1)} style={styles.backBtn}>&larr; Back</button>
                    </div>
                )}

                {step === 3 && (
                    <div style={styles.body}>
                        <h4 style={styles.title}>🔢 Choose Vehicle Registration</h4>
                        <div style={regOption === 'existing' ? styles.optionActive : styles.option} onClick={() => setRegOption('existing')}>
                            <strong>Keep Existing Number Plate:</strong> {selectedBike?.reg_number || 'N/A'}
                        </div>
                        <div style={regOption === 'new' ? styles.optionActive : styles.option} onClick={() => setRegOption('new')}>
                            <strong>Register New Number Plate</strong>
                        </div>
                        {regOption === 'new' && <input placeholder="Enter New Registration Number..." value={customRegNo} onChange={e => setCustomRegNo(e.target.value)} style={styles.inputLarge} />}
                        
                        <div style={styles.footer}>
                            <button onClick={() => setStep(2)} style={styles.backBtn}>&larr; Back</button>
                            <button onClick={() => setStep(4)} style={styles.nextBtn}>Next Step &rarr;</button>
                        </div>
                    </div>
                )}

                {step === 4 && (
                    <div style={styles.body}>
                        <h4 style={styles.title}>💰 Billing & Split Payment Details</h4>
                        
                        <div style={styles.formGrid}>
                            <div>
                                <label style={styles.label}>Base Sale Price (PKR)</label>
                                <input type="number" value={salePrice} onChange={e => setSalePrice(e.target.value)} style={styles.input} placeholder="0.00" />
                            </div>
                            <div>
                                <label style={styles.label}>Sale Type</label>
                                <select value={saleType} onChange={e => setSaleType(e.target.value)} style={styles.input}>
                                    <option value="Full">Full Payment</option>
                                    <option value="Installment">Installment Plan</option>
                                </select>
                            </div>
                        </div>

                        {/* Split Payment Section (Cash & Bank Only - No Balance Shown) */}
                        <div style={{ marginTop: '10px', marginBottom: '15px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                <label style={{ fontWeight: 'bold', color: '#1e293b' }}>Payment Breakdown (Cash / Bank Split)</label>
                                <button type="button" onClick={addPaymentRow} style={styles.addPaymentBtn}>+ Add Row</button>
                            </div>

                            {payments.map((pay, index) => {
                                const filteredAccounts = accounts.filter(acc => {
                                    if (pay.method === 'Cash') {
                                        return acc.account_type && acc.account_type.toLowerCase() === 'cash';
                                    } else {
                                        return acc.account_type && acc.account_type.toLowerCase() === 'bank';
                                    }
                                });

                                return (
                                    <div key={index} style={styles.paymentRow}>
                                        <select 
                                            value={pay.method} 
                                            onChange={e => handlePaymentChange(index, 'method', e.target.value)} 
                                            style={styles.paymentSelect}
                                        >
                                            <option value="Cash">Cash</option>
                                            <option value="Bank">Bank</option>
                                        </select>

                                        <select 
                                            value={pay.account_id} 
                                            onChange={e => handlePaymentChange(index, 'account_id', e.target.value)} 
                                            style={styles.paymentSelect}
                                        >
                                            <option value="">-- Select {pay.method} Account --</option>
                                            {filteredAccounts.map(acc => (
                                                <option key={acc.id} value={acc.id}>{acc.title}</option>
                                            ))}
                                        </select>

                                        <input 
                                            type="number" 
                                            placeholder="Amount" 
                                            value={pay.amount} 
                                            onChange={e => handlePaymentChange(index, 'amount', e.target.value)} 
                                            style={styles.paymentInput} 
                                        />

                                        {payments.length > 1 && (
                                            <button type="button" onClick={() => removePaymentRow(index)} style={styles.removeRowBtn}>&times;</button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Live Auto Calculation Box Before Confirmation */}
                        <div style={styles.summaryBox}>
                            <div style={styles.summaryRow}>
                                <span>Base Price:</span>
                                <strong>Rs. {parseFloat(salePrice || 0).toLocaleString()}</strong>
                            </div>
                            <div style={styles.summaryRow}>
                                <span>GST ({taxRate}%):</span>
                                <strong>Rs. {gstAmount.toLocaleString()}</strong>
                            </div>
                            <div style={{...styles.summaryRow, borderTop:'1px dashed #cbd5e1', paddingTop:'8px', marginTop:'4px'}}>
                                <span style={{color:'#1e293b', fontWeight:'bold'}}>Total Net Payable:</span>
                                <span style={{color:'#2563eb', fontWeight:'bold', fontSize:'16px'}}>Rs. {totalPrice.toLocaleString()}</span>
                            </div>
                            <div style={styles.summaryRow}>
                                <span>Total Paid Now:</span>
                                <span style={{color:'#16a34a', fontWeight:'bold'}}>Rs. {totalPaid.toLocaleString()}</span>
                            </div>
                            <div style={{...styles.summaryRow, background: remainingBalance > 0 ? '#fef2f2' : '#f0fdf4', padding:'8px', borderRadius:'6px', marginTop:'6px'}}>
                                <span style={{fontWeight:'bold', color: remainingBalance > 0 ? '#dc2626' : '#16a34a'}}>Remaining Balance (Baqi):</span>
                                <strong style={{color: remainingBalance > 0 ? '#dc2626' : '#16a34a'}}>Rs. {remainingBalance.toLocaleString()}</strong>
                            </div>
                        </div>

                        {saleType === 'Installment' && (
                            <div style={styles.installmentPanel}>
                                <div style={{display:'flex', gap:'15px', alignItems:'center'}}>
                                    <label>Tenure (Months):</label>
                                    <input type="number" value={totalMonths} onChange={e => setTotalMonths(e.target.value)} style={{...styles.input, width:'80px', marginBottom:0}} />
                                </div>
                                <p style={{marginTop:'10px', fontWeight:'bold', color:'#2563eb'}}>Monthly Installment: Rs. {Math.round(monthlyAmount).toLocaleString()}</p>
                            </div>
                        )}

                        <div style={styles.footer}>
                            <button onClick={() => setStep(3)} style={styles.backBtn}>&larr; Back</button>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button onClick={() => handleFinalSubmit(false)} style={styles.postBtn}>Post</button>
                                <button onClick={() => handleFinalSubmit(true)} style={styles.doneBtn}>Post &amp; Print 🖨️</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const styles = {
    overlay: { position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.85)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 5000, backdropFilter: 'blur(5px)' },
    modal: { background: '#fff', padding: '30px', borderRadius: '16px', width: '95vw', height: '90vh', maxWidth: '1100px', position: 'relative', boxShadow: '0 25px 35px -5px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column', overflowY: 'auto' },
    body: { flex: 1, overflowY: 'auto', paddingRight: '5px' },
    closeBtn: { position: 'absolute', right: '20px', top: '20px', border: 'none', background: '#f1f5f9', width: '38px', height: '38px', borderRadius: '50%', cursor: 'pointer', fontSize: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' },
    stepper: { display: 'flex', justifyContent: 'space-around', marginBottom: '25px', borderBottom: '1px solid #f1f5f9', paddingBottom: '15px' },
    stepActive: { color: '#2563eb', fontWeight: 'bold', fontSize: '14px', borderBottom: '2px solid #2563eb', paddingBottom: '13px' },
    stepInactive: { color: '#94a3b8', fontSize: '14px' },
    title: { margin: '0 0 20px 0', color: '#1e293b', fontSize: '18px' },
    inputLarge: { width: '100%', padding: '14px', marginBottom: '15px', borderRadius: '10px', border: '1px solid #cbd5e1', outlineColor: '#2563eb', fontSize: '15px', boxSizing: 'border-box' },
    input: { width: '100%', padding: '12px', marginBottom: '15px', borderRadius: '10px', border: '1px solid #cbd5e1', outlineColor: '#2563eb', boxSizing: 'border-box' },
    label: { fontSize: '12px', color: '#64748b', marginBottom: '5px', display: 'block', fontWeight: 'bold' },
    list: { maxHeight: '350px', overflowY: 'auto', marginBottom: '15px', display: 'flex', flexDirection: 'column', gap: '8px' },
    cardItem: { padding: '14px 18px', background: '#f8fafc', borderRadius: '10px', cursor: 'pointer', border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'all 0.2s' },
    selectTag: { background: '#eff6ff', color: '#2563eb', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold' },
    option: { padding: '15px', border: '1px solid #cbd5e1', borderRadius: '12px', marginBottom: '10px', cursor: 'pointer', background: '#fff' },
    optionActive: { padding: '15px', border: '2px solid #2563eb', background: '#eff6ff', borderRadius: '12px', marginBottom: '10px', fontWeight: 'bold' },
    formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' },
    paymentRow: { display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'center' },
    paymentSelect: { flex: 1, padding: '11px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff' },
    paymentInput: { width: '130px', padding: '11px', borderRadius: '8px', border: '1px solid #cbd5e1' },
    addPaymentBtn: { background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' },
    removeRowBtn: { background: '#fee2e2', color: '#dc2626', border: 'none', width: '34px', height: '34px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '18px' },
    summaryBox: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '15px', marginBottom: '15px' },
    summaryRow: { display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '14px', color: '#475569' },
    footer: { display: 'flex', justifyContent: 'space-between', marginTop: '20px' },
    nextBtn: { background: '#2563eb', color: '#fff', border: 'none', padding: '12px 25px', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer' },
    backBtn: { background: '#f1f5f9', border: 'none', padding: '12px 25px', borderRadius: '10px', cursor: 'pointer', color: '#475569', fontWeight: 'bold' },
    postBtn: { background: '#2563eb', color: '#fff', border: 'none', padding: '12px 20px', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer', fontSize: '15px' },
    doneBtn: { background: '#10b981', color: '#fff', border: 'none', padding: '12px 25px', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer', fontSize: '15px' },
    installmentPanel: { background: '#f0fdf4', padding: '15px', borderRadius: '15px', border: '1px solid #dcfce7', marginBottom: '15px' }
};

export default SaleBikeModal;